import { Outlet, Route, BrowserRouter as Router, Routes } from 'react-router';
import { Flip, ToastContainer } from 'react-toastify';
import { BrokenTools } from './pages/broken-tools/brokenTools.tsx';
import { Index } from './pages/index/index.tsx';
import { LiveGraphs } from './pages/live-graphs/liveGraphs.tsx';
import { Login } from './pages/login/login.tsx';
import { MachineLog } from './pages/machine-log/machineLog.tsx';
import { ManagementView } from './pages/management-view/managementView.tsx';
import { Overlook } from './pages/overlook/overlook.tsx';
import { PotentialScraps } from './pages/potential-scraps/potentialScraps.tsx';
import { ProtocolList } from './pages/protocol-list/protocolList.tsx';
import { ProtocolView } from './pages/protocol-view/protocolView.tsx';
import { RecordList } from './pages/record-list/recordList.tsx';
import { RecordView } from './pages/record-view/recordView.tsx';
import { ReportGraphs } from './pages/report-graphs/reportGraphs.tsx';
import { Resources } from './pages/resources/resources.tsx';
import { SpcDetails } from './pages/spc-details/spcDetails.tsx';
import { SpcList } from './pages/spc-list/spcList.tsx';

import 'react-toastify/dist/ReactToastify.css';
import { AppVersionCheck } from './components/appVersionCheck.tsx';
import { LoadingIndicator } from './components/loadingIndicator.tsx';
import { useGetLogin } from './hooks/useGetLogin.ts';
import { useRedirectToLogin } from './hooks/useRedirectToLogin.ts';
import { IoqcList } from './pages/ioqc-list/ioqcList.tsx';
import { MeasureInterval } from './pages/measure-interval/measureInterval.tsx';
import { ProductPage } from './pages/product/productPage.tsx';
import { TasksPage } from './pages/tasks/tasksPage.tsx';
import { WebToolsPage } from './pages/web-tools/webToolsPage.tsx';
import './style.scss';

export function App() {
    const { isLoggedIn, isAdmin, isFirstLoading, inFirstLoadError } = useGetLogin();

    if (isFirstLoading) {
        return (
            <div className='app-loading'>
                <LoadingIndicator title='Loading' text='Connecting to server...' />
            </div>
        );
    }
    if (inFirstLoadError) {
        return <div>Error connecting to server.</div>;
    }
    return (
        <>
            <Router>
                <Routes>
                    <Route path='/login' element={<Login />} />

                    <Route element={<ProtectedRoute isLoggedIn={isLoggedIn!} />}>
                        <Route path='/' element={<Index />} />
                        <Route path='/measurement-interval' element={<MeasureInterval />} />
                        <Route path='/machine-log' element={<MachineLog />} />
                        <Route path='/overlook' element={<Overlook />} />
                        <Route path='/potential-scraps' element={<PotentialScraps />} />
                        <Route path='/management-view' element={<ManagementView />} />
                        <Route path='/live-graphs' element={<LiveGraphs />} />
                        <Route path='/broken-tools' element={<BrokenTools />} />
                        <Route path='/record-list' element={<RecordList />} />
                        <Route path='/record-view' element={<RecordView />} />
                        <Route path='/ioqc-list' element={<IoqcList />} />
                        <Route path='/tasks' element={<TasksPage />} />
                        <Route path='/product' element={<ProductPage />} />
                        <Route path='/web-tools' element={<WebToolsPage />} />

                        <Route element={<AdminRoute isAdmin={isAdmin!} />}>
                            <Route path='/protocol-list' element={<ProtocolList />} />
                            <Route path='/protocol-view' element={<ProtocolView />} />
                            <Route path='/resources' element={<Resources />} />
                            <Route path='/reporting-tool' element={<ReportGraphs />} />
                            <Route path='/spc-list' element={<SpcList />} />
                            <Route path='/spc-details' element={<SpcDetails />} />
                        </Route>
                    </Route>

                    <Route path='*' element={<NotFound />} />
                </Routes>
            </Router>

            <ToastContainer
                position='bottom-center'
                autoClose={4000}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick
                pauseOnFocusLoss
                pauseOnHover
                theme='light'
                transition={Flip}
            />

            <AppVersionCheck />
        </>
    );
}


function ProtectedRoute({ isLoggedIn }: { isLoggedIn: boolean }) {
    useRedirectToLogin({ isLoggedIn });

    if (!isLoggedIn) {
        return <AccessDenied />;
    } else {
        return <Outlet />;
    }
}

function AdminRoute({ isAdmin }: { isAdmin: boolean }) {
    if (!isAdmin) {
        return <AccessDenied />;
    } else {
        return <Outlet />;
    }
}


function NotFound() {
    return <h1>Page Not Found</h1>;
}

function AccessDenied() {
    return <h1>Access Denied</h1>;
}
